import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "nocode-ai",
  name: "ノーコードAI",
  nameEn: "No-Code AI",
  seo: {
    title:
      "ノーコードAIを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "コードを書かずにAIを業務に活用するスキルを身につけるならゆめさくエンジニア。Zapier+AI、Dify、GPTsなどのノーコードAIツールで業務自動化を実現しましょう。",
    url: "/columns/nocode-ai-programming-school",
  },
  hero: {
    title: "ノーコードAIを学べる女性向けプログラミングスクール",
    englishTitle: "Learn No-Code AI",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "ノーコードAI" },
  ],
  overview: {
    heading: "ノーコードAIとは？",
    paragraphs: [
      "ノーコードAIとは、プログラミングのコードを書かずにAI技術を活用する手法の総称です。Zapier、Make（旧Integromat）、Dify、GPTsなどのツールを使い、ドラッグ＆ドロップやGUI操作だけでAIを組み込んだ業務フローを構築できます。",
      "ChatGPTのカスタムGPTs機能を使えば、自社専用のAIアシスタントをノーコードで作成でき、Difyなどのプラットフォームを使えばRAG付きのAIチャットボットも簡単に構築できます。プログラミング経験がなくてもAIの恩恵を最大限に活用できる時代が来ています。",
      "ノーコードAIは特にDX（デジタルトランスフォーメーション）推進において重要なスキルです。エンジニアでなくても業務プロセスにAIを組み込み、データ入力の自動化、レポート生成、顧客対応の効率化などを実現できます。",
    ],
    useCases: [
      {
        label: "業務プロセスの自動化",
        description:
          "メール対応、データ入力、レポート作成など、繰り返し発生する業務をZapier+AIで自動化し、作業時間を大幅に削減します。",
      },
      {
        label: "カスタムAIアシスタント構築",
        description:
          "GPTsやDifyを使い、社内FAQ対応、商品提案、議事録要約など、特定の業務に特化したAIアシスタントをノーコードで構築します。",
      },
      {
        label: "データ分析・レポート自動生成",
        description:
          "AIを活用してExcelやスプレッドシートのデータを自動分析し、グラフやレポートを自動生成するワークフローを構築します。",
      },
      {
        label: "マーケティング自動化",
        description:
          "SNS投稿の自動生成、メルマガの最適化、顧客セグメント別の文面自動作成など、マーケティング業務にAIを活用します。",
      },
    ],
  },
  whyLearn: {
    heading: "ノーコードAIを学ぶべき3つの理由",
    items: [
      {
        title: "プログラミング不要でAIを業務に即活用できる",
        description:
          "コードを書く必要がないため、プログラミング経験がなくてもすぐにAIを業務に導入できます。学んだその日から業務効率化を始められるのが最大の魅力です。",
      },
      {
        title: "DX推進人材として市場価値が急上昇",
        description:
          "あらゆる企業がDXを推進する中、AIを活用した業務改善を提案・実行できる人材は非常に不足しています。ノーコードAIスキルは、非エンジニアのキャリアアップにも直結します。",
      },
      {
        title: "プログラミング学習への橋渡しになる",
        description:
          "ノーコードでAIの概念や仕組みを理解した後、Python等のプログラミング学習に進むことで、より高度なAI開発にもスムーズにステップアップできます。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでノーコードAIを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、Phase 4の応用カリキュラムとしてノーコードAIを学びます。まずAI技術の基礎を理解した上で、実際のビジネスシーンを想定したノーコードAIツールの活用法を実践的に学びます。",
      "単にツールの使い方を覚えるだけでなく、「どの業務にAIを適用すべきか」「どのツールを組み合わせるのが最適か」という設計思考も含めて学ぶため、現場で即戦力となるスキルが身につきます。",
    ],
    topics: [
      "AI活用の基礎知識とプロンプト設計",
      "GPTsによるカスタムAIアシスタント構築",
      "Difyを使ったRAGチャットボット開発",
      "Zapier + AIによる業務自動化フロー構築",
      "Make（旧Integromat）によるマルチステップ自動化",
      "AI×ノーコードの業務改善提案手法",
      "実践プロジェクト：業務自動化AIシステムの設計・構築",
    ],
  },
  careerPaths: {
    heading: "ノーコードAIを活かせるキャリア",
    items: [
      {
        title: "DXコンサルタント",
        description:
          "企業のデジタルトランスフォーメーションを推進する職種です。ノーコードAIツールを活用した業務改善提案から導入支援まで、幅広く対応します。",
        salary: "450〜800万円",
      },
      {
        title: "業務自動化スペシャリスト",
        description:
          "企業内の業務プロセスを分析し、AI×ノーコードツールによる自動化ソリューションを設計・構築する専門職です。バックオフィスのDX推進に欠かせないポジションです。",
        salary: "400〜700万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "3〜6ヶ月（ツールが多すぎて何から始めるか迷う）",
        luagate: "2〜3ヶ月（実務で使えるツールに厳選して効率学習）",
      },
      {
        label: "挫折率",
        selfStudy: "ツールの使い方は覚えても業務適用が難しい",
        luagate: "実際のビジネス課題で実践するため定着率が高い",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "チュートリアル通りの簡単な自動化で止まりがち",
        luagate: "実際の業務フローを想定した自動化システムを構築",
      },
      {
        label: "質問対応",
        selfStudy: "ツール連携のトラブルシュートに時間がかかる",
        luagate: "経験豊富なメンターがツール選定から設計まで指導",
      },
      {
        label: "就職サポート",
        selfStudy: "ノーコードAIスキルのアピール方法がわからない",
        luagate: "DX推進企業とのネットワークを活かした就職支援",
      },
    ],
  },
  faq: [
    {
      question: "プログラミング経験がなくてもノーコードAIを学べますか？",
      answer:
        "はい、ノーコードAIはプログラミング不要で学べるのが最大の特徴です。GUI操作でAIを活用するため、IT初心者の方でも安心して始められます。ゆめさくエンジニアでは基礎から丁寧にサポートします。",
    },
    {
      question: "ノーコードAIだけで就職・転職できますか？",
      answer:
        "DXコンサルタントや業務改善担当など、ノーコードAIスキルが直接活きる職種は増えています。さらにプログラミングスキルを組み合わせることで、より幅広いキャリアの選択肢が広がります。",
    },
    {
      question: "ノーコードAIツールの利用料金はかかりますか？",
      answer:
        "多くのツールには無料プランがあり、学習段階では無料枠で十分に試せます。Zapierの無料プランやGPTsの基本機能など、コストをかけずに始められる環境が整っています。",
    },
    {
      question: "ノーコードAIとプログラミングによるAI開発の違いは？",
      answer:
        "ノーコードAIは素早く業務に導入でき、非エンジニアでも扱える手軽さが魅力です。一方、プログラミングによるAI開発はカスタマイズの自由度が高く、大規模なシステム構築に適しています。ゆめさくエンジニアでは両方のアプローチを段階的に学べます。",
    },
  ],
  relatedLanguages: [
    { slug: "generative-ai", name: "生成AI" },
    { slug: "prompt-engineering", name: "プロンプトエンジニアリング" },
    { slug: "dx-literacy", name: "DXリテラシー" },
    { slug: "python", name: "Python" },
  ],
};
